// @ts-nocheck
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { nodeInfo } from './studioState';


export const c = (...parts) =>
  parts
    .flat()
    .filter(Boolean)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => `ags-${n}`)
    .join(' ');


export const StudioContext = createContext(null);
export const useStudio = () => useContext(StudioContext);


export function useTouch(key) {
  const { touch } = useStudio();
  return { onBlur: () => touch(key) };
}

export const Required = () => (
  <span className="ags-r" aria-hidden="true">
    *
  </span>
);

export function Field({ label, required, optional, htmlFor, note, error, count, max, children, style }) {
  return (
    <div className={c('field')} style={style}>
      {label && (
        <label htmlFor={htmlFor}>
          {label} {required && <Required />}
          {optional && <span className={c('note')}>Optional</span>}
        </label>
      )}
      {children}
      {count !== undefined && (
        <div className={c('cnt', count >= max && 'max')}>
          <span>{note}</span>
          <span>
            {count} of {max}
          </span>
        </div>
      )}
      {count === undefined && note && !error && (
        <div className={c('note')} style={{ marginTop: 6 }}>
          {note}
        </div>
      )}
      <Message error={error} />
    </div>
  );
}


export function Message({ error, ok }) {
  if (!error && !ok) return null;
  return (
    <div className={c('msg show', error ? 'err' : 'ok')} role={error ? 'alert' : undefined}>
      {error ? <AlertCircle className="ags-i" /> : <Check className="ags-i" />}
      <span>{error || ok}</span>
    </div>
  );
}

const TAGS = {
  required: ['req', 'Required'],
  done: ['done', 'Done'],
  set: ['done', 'Set'],
  on: ['done', 'On'],
  attention: ['err', 'Needs attention'],
  optional: ['opt', 'Optional'],
  off: ['opt', 'Off'],
  default: ['opt', 'Default'],
  paused: ['paused', 'Paused'],
};


export function Section({ id, icon: Icon, title, sub, tag = 'optional', tagText, plainReq, ok, bad, next, onNext, children, footer }) {
  const [tone, label] = TAGS[tag] || TAGS.optional;
  return (
    <section className={c('sec', ok && 'ok', bad && 'bad')} id={`ags-s-${id}`} data-sec={id}>
      <div className={c('sh')}>
        <span className={c('ico')}>
          <Icon className="ags-i" />
        </span>
        <div>
          <h2>
            {plainReq && tag === 'required' && <span aria-hidden className={c('star')}>* </span>}
            {title}
          </h2>
          <p>{sub}</p>
        </div>
        {plainReq && tag === 'required' ? (
          <span className={c('reqpill')}>
            <b aria-hidden>!</b> Required
          </span>
        ) : (
          <span className={c('st', tone)}>{tagText || label}</span>
        )}
      </div>
      {children}
      {footer}
      {next && (
        <div className={c('nextlink')}>
          <button type="button" className={c('btn ghost')} onClick={() => onNext(next.id)}>
            Next: {next.label}
            <svg className="ags-i" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}

export function Switch({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      className={c('sw')}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Check1({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      className={c('cb', !checked && 'off')}
      onClick={() => onChange(!checked)}
    >
      {checked && <Check className="ags-i" />}
    </button>
  );
}

export function IconBtn({ icon: Icon, label, danger, onClick, disabled }) {
  return (
    <button type="button" className={c('ib', danger && 'del')} aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      <Icon className="ags-i" />
    </button>
  );
}

export function Empty({ icon: Icon, title, text, bad }) {
  return (
    <div className={c('empty', bad && 'bad')}>
      <span className={c('ei')}>
        <Icon className="ags-i" />
      </span>
      <div>
        <b>{title}</b>
        <small>{text}</small>
      </div>
    </div>
  );
}


export function Modal({ open, onClose, title, sub, children, footer, wide, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="ags">
      <div
        className={c('scrim show')}
        onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      >
        <div ref={ref} className={c('modal')} style={wide ? { width: 'min(780px,100%)' } : { width: 'min(560px,100%)' }} role="dialog" aria-modal="true" aria-label={label || title}>
          <div className={c('mh')}>
            <div>
              <h2>{title}</h2>
              {sub && <p>{sub}</p>}
            </div>
            <button type="button" className={c('ib')} aria-label="Close" onClick={onClose}>
              <X className="ags-i" />
            </button>
          </div>
          {children}
          {footer && <div className={c('mf')}>{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}


export function useNode(id) {
  const { formState, touched, attempted, flowGenerated, readOnly } = useStudio();
  const [tone, label] = nodeInfo(id, formState, { touched, attempted, flowGenerated, readOnly });
  return { tone, label, ok: tone === 'ok', bad: tone === 'bad' };
}


export const reqTag = (n) => (n.ok ? 'done' : n.bad ? 'attention' : 'required');


export function useShown() {
  const { touched, attempted } = useStudio();
  return (key) => attempted || !!touched[key];
}


export function Carousel({ children, label, selectedSelector = '[aria-checked="true"]', deps = [] }) {
  const ref = useRef(null);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(false);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setLeft(el.scrollLeft > 4);
    setRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [update, ...deps]);

  useEffect(() => {
    const el = ref.current;
    const tile = el?.querySelector(selectedSelector);
    if (!el || !tile) return;
    if (tile.offsetLeft + tile.offsetWidth > el.clientWidth) el.scrollLeft = Math.max(0, tile.offsetLeft - 8);
  }, deps);
  const by = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: 'smooth' });
  return (
    <div className={c('carousel')}>
      {left && (
        <button type="button" className={c('arrow l')} aria-label={`Previous ${label}`} onClick={() => by(-1)}>
          <ChevronLeft className="ags-i" />
        </button>
      )}
      <div ref={ref} className={c('tiles')} role="radiogroup" aria-label={label}>
        {children}
      </div>
      {right && (
        <button type="button" className={c('arrow r')} aria-label={`Next ${label}`} onClick={() => by(1)}>
          <ChevronRight className="ags-i" />
        </button>
      )}
    </div>
  );
}


export const Skeleton = ({ n = 3, w = 168, h = 120 }) => (
  <div style={{ display: 'flex', gap: 12, padding: '6px 4px 14px', overflow: 'hidden' }}>
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} style={{ flex: `0 0 ${w}px`, height: h, borderRadius: 16, background: '#DCE3F0', opacity: 0.6 }} />
    ))}
  </div>
);
